import React, { useEffect, useState } from 'react';
import { PaginatedResponseData } from '../../services/api/client';
import { Server } from '../../types/order';
import { loadOrders } from '../../services/api/OrderService';
import { Dropdown, Flex, Input, Table, Tag } from 'antd';
import { OrderFulfillmentStatus, OrderPaymentStatus, PaymentMethod } from '../../types/constants';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CreditCardOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  SyncOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import useHelpers from '../../hooks/utility/useHelpers';
import { collect } from 'collect.js';
import ClickToCopy from '../../components/helpers/ClickToCopy';
import DateRangePicker, {
  Period,
  Variant,
  dateRangeFactory,
} from '../../components/filters/DateRangePicker';
import useData from '../../hooks/utility/useData';
import { debounce } from 'lodash';
import StoreSelector from '../../components/filters/StoreSelector';
import PaginationHelpers from '../../services/utils/pagination/PaginationHelpers';
import OrderDetailsModal from '../../modals/OrderDetailsModal';

export default function OrderListPage() {
  const { formatCurrency, formatDateTime } = useHelpers();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PaginatedResponseData<Server.OrderData>>(
    PaginationHelpers.initPaginationData(),
  );

  const [selectedOrder, setSelectedOrder] = useState(null);

  const { data: filters, updateData: updateFilter } = useData({
    stores: [],
    status: [],
    customer_details: null,
    ...dateRangeFactory(Period.Today),
  });

  const debounced = debounce(async () => loadData(data.meta.current_page), 500);

  useEffect(() => {
    debounced();

    return () => debounced.cancel();
  }, [filters]);

  async function loadData(page: number) {
    setLoading(true);

    loadOrders({
      page,
      filter: {
        stores: filters.stores,
        created_between:
          filters.start_date && filters.end_date
            ? [filters.start_date.format(), filters.end_date.format()]
            : undefined,
        customer_details: filters.customer_details,
      },
    })
      .then((_data) => setData(_data.data))
      .catch((error) => console.error('Failed to load orders', { error }))
      .finally(() => setLoading(false));
  }

  return (
    <>
      <h1>Orders</h1>
      <div className="mt-sm">
        <DateRangePicker
          defaultFilter={Period.Today}
          variant={Variant.RadioGroup}
          onChange={(value) => {
            updateFilter('start_date', value.start_date);
            updateFilter('end_date', value.end_date);
          }}
        />
        <Input.Search
          value={filters.customer_details}
          onChange={(e) => updateFilter('customer_details', e.target.value)}
          className="mt-sm"
          placeholder="Customer details"
        />
        <StoreSelector
          mode="multiple"
          value={filters.stores}
          onChange={(value: string[]) => updateFilter('stores', value)}
          placeholder="Select stores"
          className="mt-sm w-full"
        />
        <Table
          dataSource={data.data}
          loading={loading}
          rowKey="id"
          className="mt-sm overflow-x-auto"
          pagination={{
            current: data.meta.current_page,
            total: data.meta.total,
            pageSize: data.meta.per_page,
            onChange: (page) => loadData(page),
            rootClassName: 'tw-overflow-hidden',
          }}
          columns={[
            {
              key: 'actions',
              fixed: 'left',
              render: (_, item) => (
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'show_order_details',
                        label: 'Show order details',
                        onClick: () => setSelectedOrder(item),
                        icon: <InfoCircleOutlined />,
                      },
                    ],
                  }}
                >
                  <MoreOutlined />
                </Dropdown>
              ),
            },
            {
              key: '#',
              title: '#',
              render: (item) =>
                item.payment_status === OrderPaymentStatus.Pending
                  ? item.id
                  : item.fulfillment.number,
            },
            {
              key: 'restaurant',
              title: 'Restaurant',
              dataIndex: ['company', 'name'],
            },
            {
              key: 'placed_at',
              title: 'Order placed',
              render: (_, item) => formatDateTime(item.created_at),
            },
            {
              key: 'fulfillment_status',
              title: 'Fulfillment status',
              render: (_, item) => {
                if (item.payment_status === OrderPaymentStatus.Pending) {
                  return (
                    <Tag color="#000000" icon={<CreditCardOutlined />}>
                      Awaiting payment
                    </Tag>
                  );
                }

                const color = {
                  [OrderFulfillmentStatus.Created]: 'warning',
                  [OrderFulfillmentStatus.Reviewed]: 'warning',
                  [OrderFulfillmentStatus.Accepted]: 'processing',
                  [OrderFulfillmentStatus.Preparing]: 'processing',
                  [OrderFulfillmentStatus.OnDelivery]: 'processing',
                  [OrderFulfillmentStatus.Ready]: 'processing',
                  [OrderFulfillmentStatus.Picked]: 'success',
                  [OrderFulfillmentStatus.Rejected]: 'error',
                }[item.fulfillment.status];

                const icon = {
                  [OrderFulfillmentStatus.Created]: <ClockCircleOutlined />,
                  [OrderFulfillmentStatus.Reviewed]: <ClockCircleOutlined />,
                  [OrderFulfillmentStatus.Accepted]: <SyncOutlined spin />,
                  [OrderFulfillmentStatus.Preparing]: <SyncOutlined spin />,
                  [OrderFulfillmentStatus.OnDelivery]: <SyncOutlined spin />,
                  [OrderFulfillmentStatus.Ready]: <SyncOutlined spin />,
                  [OrderFulfillmentStatus.Picked]: <CheckCircleOutlined />,
                  [OrderFulfillmentStatus.Rejected]: <CloseCircleOutlined />,
                }[item.fulfillment.status];

                return (
                  <Flex gap="4px 0">
                    <Tag color={color} icon={icon}>
                      {item.fulfillment.status}
                    </Tag>
                    {item.fulfillment.status === OrderFulfillmentStatus.Rejected &&
                      item.rejection_reason === 'auto' && (
                        <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                          Rejected automatically
                        </Tag>
                      )}
                    {item.fulfillment.status === OrderFulfillmentStatus.Rejected &&
                      item.rejection_reason !== 'auto' && (
                        <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                          Rejected manually
                        </Tag>
                      )}
                  </Flex>
                );
              },
            },
            {
              key: 'method',
              title: 'Method',
              dataIndex: 'method',
            },
            {
              key: 'items',
              title: 'Items',
              render: (_, item) => collect(item.line_items).sum('quantity'),
            },
            {
              key: 'total',
              title: 'Total',
              render: (_, item) => formatCurrency(item.total),
            },
            {
              key: 'customer',
              title: 'Customer',
              render: (_, item) =>
                `${item.customer_first_name ?? ''} ${item.customer_last_name ?? ''}`.trim(),
            },
            {
              key: 'payment_status',
              title: 'Payment status',
              render: (_, item) => {
                const paymentStatus =
                  {
                    [OrderPaymentStatus.Pending]: OrderPaymentStatus.Pending,
                    [OrderPaymentStatus.Paid]: OrderPaymentStatus.Paid,
                    [OrderPaymentStatus.Refunded]: OrderPaymentStatus.Refunded,
                  }[item.payment_status] ?? OrderPaymentStatus.PartiallyRefunded;

                const color = {
                  [OrderPaymentStatus.Pending]: 'default',
                  [OrderPaymentStatus.Paid]: 'success',
                  [OrderPaymentStatus.PartiallyRefunded]: 'warning',
                  [OrderPaymentStatus.Refunded]: 'error',
                }[paymentStatus];

                const icon = {
                  [OrderPaymentStatus.Pending]: <ClockCircleOutlined />,
                  [OrderPaymentStatus.Paid]: <CheckCircleOutlined />,
                  [OrderPaymentStatus.PartiallyRefunded]: <WarningOutlined />,
                  [OrderPaymentStatus.Refunded]: <CloseCircleOutlined />,
                }[paymentStatus];

                return (
                  <Flex gap="4px 0">
                    <Tag color={color} icon={icon}>
                      {item.payment?.payment_method_id === PaymentMethod.Cash
                        ? 'Cash payment'
                        : paymentStatus}
                    </Tag>
                  </Flex>
                );
              },
            },
            {
              key: 'id',
              title: 'ID',
              render: (_, item) => <ClickToCopy>{item.id}</ClickToCopy>,
            },
          ]}
        />
      </div>
      {Boolean(selectedOrder) && (
        <OrderDetailsModal data={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}
    </>
  );
}
